import first from 'lodash/fp/first';
import isNil from 'lodash/fp/isNil';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Unit } from '../../../common/constants';
import { getBatteryLevelPercentage } from '../../../common/tourUtils';
import { convertISOToRelativeDate, getBatteryColor, getBatteryIcon } from '../../../common/utils';
import { useAppDispatch, useAppSelector } from '../../../configuration/setup/hooks';
import { trace } from '../../../configuration/setup/trace';
import { resolveAddress } from '../../../features/fetchData/resolveAddress';
import { selectIsFirstWaypointVehicleLocation, selectOrderedWaypoints } from '../../../store/search/searchSelectors';
import { selectItineraries, selectSelectedItinerary } from '../../../store/tour/tourSelectors';
import { selectLocale } from '../../../store/lang/langSelectors';
import { selectBatteryCapacity } from '../../../store/vehicleProfile/vehicleProfileSelectors';

export const DeparturePanel = ({ cargoAtDeparture }: { cargoAtDeparture?: number }) => {
    const dispatch = useAppDispatch();
    const waypoints = useAppSelector(selectOrderedWaypoints);
    const isFirstWpVehicleLocation = useAppSelector(selectIsFirstWaypointVehicleLocation);
    const itineraries = useAppSelector(selectItineraries);
    const selectedItinerary = useAppSelector(selectSelectedItinerary);
    const locale = useAppSelector(selectLocale);
    const batteryCapacity = useAppSelector(selectBatteryCapacity).value;
    const [vehicleAddress, setVehicleAddress] = useState<string>();

    const departureWaypoint = first(waypoints);
    const currentItinerary = selectedItinerary !== undefined ? itineraries?.at(selectedItinerary) : undefined;
    const departureTime = currentItinerary?.sections?.at(0)?.departureTime;
    const batteryPct = getBatteryLevelPercentage(
        batteryCapacity,
        first(currentItinerary?.sections)?.evBatteryCapacityDeparture
    );

    const date = convertISOToRelativeDate(locale, departureTime);

    const getVehicleLocation = async () => {
        try {
            const eventObj = {
                type: 'EVENT_SMART_ROUTING',
                payload: {
                    coordinates: departureWaypoint?.coordinates,
                },
            };

            const resolvedAddress = await dispatch(resolveAddress(eventObj));
            setVehicleAddress(resolvedAddress);
        } catch (error) {
            trace.error('Could not resolve vehicle address: ', error);
        }
    };

    useEffect(() => {
        if (isFirstWpVehicleLocation) {
            getVehicleLocation();
        }
    });

    return (
        <li className='timeline-element' data-testid='timeline-element'>
            <div className='timeline-date margin-right-10'>
                <div className='border border-width-3 border-color-white'>
                    <span className='badge badge-lg badge-muted padding-5'>
                        <span className='rioglyph rioglyph-start' />
                    </span>
                </div>
            </div>
            <div className='timeline-content margin-bottom-15'>
                <div className='text-medium text-size-16 text-color-darker margin-top-5 margin-bottom-5'>
                    <FormattedMessage id='intl-msg:smartRoute.tour.departure' />
                </div>
                <div className='display-flex flex-column gap-2'>
                    {date && (
                        <div>
                            <span className='rioglyph rioglyph-calendar text-color-gray margin-right-5' />
                            <span>{date}</span>
                        </div>
                    )}
                    <div>
                        <span className='rioglyph rioglyph-map-marker text-color-gray margin-right-5' />
                        <span>{isFirstWpVehicleLocation ? vehicleAddress : departureWaypoint?.addressLabel}</span>
                    </div>
                    {!isNil(cargoAtDeparture) && (
                        <div>
                            <span className='rioglyph rioglyph-weight text-color-gray margin-right-5' />
                            <span>{cargoAtDeparture}</span>
                            <span>&nbsp;{Unit.weight}</span>
                        </div>
                    )}
                    {!isNil(batteryPct) && (
                        <div className={`${getBatteryColor(batteryPct)}`}>
                            <span className={`${getBatteryIcon(batteryPct)} margin-right-5`} />
                            <span>{batteryPct}</span>
                            <span>&nbsp;{Unit.pct}</span>
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
};
